import * as React from "react"
import {CmsContent} from "../../Model/CmsContent/CmsContent";
import HeaderFooterWrapper from "../../Components/Navigation/HeaderFooterWrapper";
import {optHtmlContent, renderDynamicContent} from "../../Utilities/CmsUtilities";
import SuperdrugOrderSummaryCard from "../../Components/Superdrug/Card/SuperdrugOrderSummaryCard";
import {ApplicationContext} from "../../ApplicationContext";
import {useNavigate} from "react-router-dom";
import {SimOnlyPlan} from "../../Model/Sales/SimOnlyPlan";
import CmsStaticHtmlContent from "../../Components/CmsStaticHtmlContent/CmsStaticHtmlContent";
import {SALES_PORTAL_ROUTES} from "../../Routes/SalesPortalRoutes";
import {fetchSimOnlyPlanByCode, fetchSimOnlyPlanByPlanCodeAndPromoCode} from "../../Api/Sales/SimOnlyPlanApi";
import {isRequestError} from "../../Model/RequestError";
import {isOrderConfirmation, OrderConfirmation} from "../../Model/Sales/OrderConfirmation";
import {readLocalStorageItem} from "../../Utilities/LocalStorageUtilities";
import {PlanPrice} from "../../Model/Sales/PlanPrice";
import {fetchPriceForPlanWithPromo} from "../../Api/Sales/PricesApi";
import {readSessionItem} from "../../Utilities/SessionUtilities";
import {isSimDetails} from "../../Model/Sim/SimDetails";

interface SuperdrugSimInHandOrderConfirmationPageProps {

    /**
     * Array of all content currently stored in the CMS that has been made available to the Sales Portal.
     */
    readonly cmsContent: CmsContent[]

}

/**
 * Function will render the Superdrug-specific SIM-in-hand confirmation page which will show the customer a brief
 * summary of what they have just signed up to.
 */
const SuperdrugSimInHandOrderConfirmationPage = (props: SuperdrugSimInHandOrderConfirmationPageProps): JSX.Element | null => {

    const applicationContext = React.useContext(ApplicationContext)
    const navigate = useNavigate()

    const [componentData, setComponentData] = React.useState<{
        readonly orderConfirmation: OrderConfirmation
        readonly selectedPlan: SimOnlyPlan
        readonly selectedPlanPriceWithPromo: PlanPrice | null
        readonly paidInStorePlan: SimOnlyPlan | null
        readonly paidInStorePriceWithPromo: PlanPrice | null
    }>()

    /**
     * Function will render a card with some text explaining that we are getting their SIM card ready.
     */
    const GettingYourSimReadyCard = (): JSX.Element | null => {
        const optGettingYouRSimReadyCard = optHtmlContent(props.cmsContent, "sp-card-getting-your-sim-ready")

        if (optGettingYouRSimReadyCard && componentData) {
            return renderDynamicContent(optGettingYouRSimReadyCard, [
                {key: "EMAIL_ADDRESS", value: componentData.orderConfirmation.email}
            ])
        } else {
            return null
        }
    }

    /**
     * Function will fetch all the information needed for this component before rendering it.
     */
    const setupComponent = async () => {
        const optSimDetails = readSessionItem("sim-details", isSimDetails)

        const orderConfirmation = readLocalStorageItem("order-confirmation", isOrderConfirmation)
        if (!orderConfirmation) {
            // Shouldn't happen, but if it does, send user to self-care instead of showing nothing.
            window.location.replace(applicationContext.selfcareWebappPublicUrl!!)
            return
        }

        const maybePlan = await fetchSimOnlyPlanByCode(
            applicationContext.reseller, applicationContext.salesPortalApiDomain, orderConfirmation.packageCode)
        if (isRequestError(maybePlan)) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            return
        }

        let inStorePlan: SimOnlyPlan | null = null
        const isLeadGen = optSimDetails?.leadGenerator ?? false
        let planPriceWithPromo: PlanPrice | null
        let inStorePriceWithPromo: PlanPrice | null
        if (!isLeadGen) {
            if (applicationContext.appConfig.signupConfiguration.defaultPlan === orderConfirmation.packageCode) {

                    inStorePlan = maybePlan

            } else {
                    const maybeInStorePlan = await fetchSimOnlyPlanByCode(
                        applicationContext.reseller, applicationContext.salesPortalApiDomain, applicationContext.appConfig.signupConfiguration.defaultPlan!!)
                    if (isRequestError(maybeInStorePlan)) {
                        navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
                        return
                    }
                    inStorePlan = maybeInStorePlan
            }
        }

            planPriceWithPromo = null
            inStorePriceWithPromo = null
            if(orderConfirmation.promoCode) {
                const maybePlanPrice = await fetchPriceForPlanWithPromo(
                    applicationContext.reseller, applicationContext.salesPortalApiDomain,
                    orderConfirmation.packageCode!!, orderConfirmation.promoCode)
                if (isRequestError(maybePlanPrice)) {
                    navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
                    return
                }
                planPriceWithPromo = maybePlanPrice
                if(!isLeadGen && inStorePlan) {
                    inStorePriceWithPromo = {...planPriceWithPromo,
                        priceToPay: planPriceWithPromo.priceToPay - inStorePlan.monthlyPrice}
                }else {
                    inStorePriceWithPromo = {...planPriceWithPromo,
                        priceToPay: planPriceWithPromo.priceToPay}
                }
            }


        setComponentData({
            selectedPlan: maybePlan,
            selectedPlanPriceWithPromo: planPriceWithPromo,
            paidInStorePlan: inStorePlan,
            paidInStorePriceWithPromo: inStorePriceWithPromo,
            orderConfirmation: orderConfirmation
        })
    }

    React.useEffect(() => {
        setupComponent()
    }, [])

    const optOrderConfirmationPage = optHtmlContent(props.cmsContent, "sp-page-sim-in-hand-order-confirmation")

    const setSelectedPlanPriceWithPromo = (planPrice: PlanPrice | null) => {
        setComponentData(prevState => ({
            ...prevState!,
            selectedPlanPriceWithPromo: planPrice
        }))
    }

    const setSelectedPlan = (simOnlyPlan: SimOnlyPlan) => {
        setComponentData(prevState => ({
            ...prevState!,
            selectedPlan: simOnlyPlan
        }))
    }

    if (!componentData || !optOrderConfirmationPage) {
        return null // Prevent page flicker while we are getting everything for the page.
    } else {
        return (
            <HeaderFooterWrapper cmsContent={props.cmsContent} variant="primary">
                {renderDynamicContent(optOrderConfirmationPage, [
                    {key: "GETTING_YOUR_SIM_READY", value: <GettingYourSimReadyCard/>},
                    {
                        key: "ORDER_SUMMARY",
                        value: <SuperdrugOrderSummaryCard
                            cmsContent={props.cmsContent}
                            selectedPlan={componentData.selectedPlan}
                            selectedPlanPriceWithPromo={componentData.selectedPlanPriceWithPromo}
                            paidInStorePlan={componentData.paidInStorePlan}
                            paidInStorePriceWithPromo={componentData.paidInStorePriceWithPromo}
                            allowEdit={false}
                            simInHand={true}
                            setSelectedPlanPriceWithPromo={setSelectedPlanPriceWithPromo}
                            setSelectedPlan={setSelectedPlan}
                            componentData={componentData}
                        />
                    },
                    {
                        key: "HEALTH_AND_BEAUTY_CARD",
                        value: <CmsStaticHtmlContent
                            cmsContent={props.cmsContent}
                            reference="sp-card-health-and-beauty"
                        />
                    },
                    {key: "DASHBOARD_URL", value: applicationContext.selfcareWebappPublicUrl}
                ])}
            </HeaderFooterWrapper>
        )
    }

}

export default SuperdrugSimInHandOrderConfirmationPage

